import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, Area, AreaChart, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { 
  Brain, Sparkles, RefreshCw, Loader2, Play, Pause, Settings2,
  AlertTriangle, CheckCircle2, FileWarning, Shield, ClipboardCheck,
  Zap, Target, Activity, Users, Building, Stethoscope, BookOpen,
  MessageSquare, Timer, ArrowRight, RotateCcw, TrendingUp, Sliders
} from 'lucide-react';
import { 
  PageHeader, MetricCard, StatusBadge, ModernCard, SectionHeader,
  modernTabsClass, modernTabTriggerClass 
} from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simulation Mode Configurations
const SIMULATION_MODES = {
  chap_survey: {
    name: 'CHAP Survey Simulation',
    description: 'Simulate a CHAP accreditation survey with real-time scenario-based questions',
    icon: ClipboardCheck,
    gradient: 'from-blue-600 to-indigo-600',
    duration: '30-45 min',
    categories: ['Leadership', 'Documentation', 'Clinical Care', 'Patient Rights', 'Safety', 'Infection Control']
  },
  unannounced_survey: {
    name: 'Mock Unannounced Survey',
    description: 'AI-powered surprise survey simulation with random timing scenarios',
    icon: AlertTriangle,
    gradient: 'from-amber-600 to-orange-600',
    duration: '20-30 min',
    categories: ['Emergency Prep', 'Documentation Access', 'Staff Competency', 'Patient Care']
  },
  recert_audit: {
    name: 'Live Recertification Audit Bot',
    description: 'Comprehensive recertification review with automated checklist validation',
    icon: Shield,
    gradient: 'from-emerald-600 to-teal-600',
    duration: '15-20 min',
    categories: ['Face-to-Face', 'CTI', 'Plan of Care', 'IDG', 'Nursing Assessments']
  },
  budget_forecast: {
    name: 'Budget Forecasting Simulation',
    description: 'Project financial scenarios with census changes and cost variations',
    icon: Building,
    gradient: 'from-green-600 to-emerald-600',
    duration: '10-15 min',
    categories: ['Revenue Projections', 'Cost Analysis', 'Census Impact', 'Break-even Analysis']
  },
  patient_load: {
    name: 'Patient Load Scenario',
    description: 'Model census fluctuations and their impact on operations',
    icon: Users,
    gradient: 'from-cyan-600 to-blue-600',
    duration: '15-20 min',
    categories: ['Admission Surge', 'Discharge Patterns', 'Capacity Planning', 'Resource Allocation']
  },
  staffing_optimization: {
    name: 'Staffing Optimization',
    description: 'Optimize staff schedules and allocations for efficiency',
    icon: Stethoscope,
    gradient: 'from-pink-600 to-rose-600',
    duration: '10-15 min',
    categories: ['FTE Analysis', 'Skill Mix', 'PRN Utilization', 'Burnout Prevention']
  },
  // Advanced CareFlow Intelligence Simulations
  workforce_whatif: {
    name: 'Workforce What-If Analysis',
    description: 'Simulate staffing changes and predict impact on operations, burnout, and capacity',
    icon: Users,
    gradient: 'from-violet-600 to-purple-600',
    duration: '10-15 min',
    categories: ['Hiring Scenarios', 'Resignation Impact', 'Team Redistribution', 'PRN Dependency'],
    careflow: true
  },
  burnout_prediction: {
    name: 'Burnout Risk Forecasting',
    description: 'AI-powered prediction of nurse burnout based on workload trends and patterns',
    icon: Activity,
    gradient: 'from-red-600 to-orange-600',
    duration: '5-10 min',
    categories: ['Individual Risk', 'Team Risk', 'Intervention Timing', 'Prevention Strategies'],
    careflow: true
  },
  census_impact: {
    name: 'Census Impact Simulation',
    description: 'Model how census changes affect nurse workload, capacity, and hiring needs',
    icon: TrendingUp,
    gradient: 'from-blue-600 to-cyan-600',
    duration: '10-15 min',
    categories: ['Census Growth', 'Census Decline', 'Seasonal Patterns', 'Emergency Surge'],
    careflow: true
  },
  hiring_timeline: {
    name: 'Hiring Timeline Simulator',
    description: 'Predict optimal hiring windows and onboarding timelines based on projected needs',
    icon: Timer,
    gradient: 'from-green-600 to-teal-600',
    duration: '5-10 min',
    categories: ['Immediate Hiring', '30-Day Projection', '90-Day Forecast', 'Budget Impact'],
    careflow: true
  },
  team_rebalancing: {
    name: 'Team Rebalancing Optimizer',
    description: 'Find optimal patient-to-nurse distribution across teams to balance workload',
    icon: Sliders,
    gradient: 'from-amber-600 to-yellow-600',
    duration: '10-15 min',
    categories: ['Caseload Balance', 'Geographic Optimization', 'Specialty Matching', 'Risk Reduction'],
    careflow: true
  }
};

// Sample survey questions for CHAP
const CHAP_QUESTIONS = [
  { category: 'Leadership', question: 'How does your organization ensure the governing body is informed of quality improvement activities?', difficulty: 'medium' },
  { category: 'Documentation', question: 'Demonstrate your process for ensuring timely completion of nursing assessments within 48 hours of admission.', difficulty: 'high' },
  { category: 'Clinical Care', question: 'Describe your pain management protocol and how effectiveness is evaluated.', difficulty: 'medium' },
  { category: 'Patient Rights', question: 'How do you ensure patients are informed of their rights and the complaint process?', difficulty: 'easy' },
  { category: 'Safety', question: 'What is your fall prevention protocol for home visits?', difficulty: 'medium' },
  { category: 'Infection Control', question: 'Explain your infection control training and monitoring process for staff.', difficulty: 'high' }
];

// Budget forecasting questions
const BUDGET_QUESTIONS = [
  { category: 'Revenue Projections', question: 'If ADC increases by 10% next quarter, what would be the estimated revenue impact?', difficulty: 'medium' },
  { category: 'Cost Analysis', question: 'How would a 15% increase in PRN utilization affect your per-patient-day cost?', difficulty: 'high' },
  { category: 'Census Impact', question: 'What is your breakeven census point and current margin of safety?', difficulty: 'medium' },
  { category: 'Break-even Analysis', question: 'If Medicare reimbursement decreases by 3%, how many additional patients would be needed to maintain current revenue?', difficulty: 'hard' }
];

// Patient load scenario questions
const PATIENT_LOAD_QUESTIONS = [
  { category: 'Admission Surge', question: 'How would your organization handle a 25% increase in referrals over 2 weeks?', difficulty: 'high' },
  { category: 'Discharge Patterns', question: 'What is your contingency plan if live discharge rate exceeds 5%?', difficulty: 'medium' },
  { category: 'Capacity Planning', question: 'At what census level would you need to hire additional staff?', difficulty: 'medium' },
  { category: 'Resource Allocation', question: 'How do you prioritize patient visits when capacity is constrained?', difficulty: 'medium' }
];

// Staffing optimization questions
const STAFFING_QUESTIONS = [
  { category: 'FTE Analysis', question: 'What is your optimal RN-to-patient ratio for your current census?', difficulty: 'medium' },
  { category: 'Skill Mix', question: 'How do you determine the right mix of RN, LPN, and CNA hours?', difficulty: 'high' },
  { category: 'PRN Utilization', question: 'When does PRN utilization become cost-ineffective compared to new hires?', difficulty: 'medium' },
  { category: 'Burnout Prevention', question: 'What triggers would indicate a staff member is at burnout risk?', difficulty: 'easy' }
];

// CareFlow Advanced Simulation Questions
const WORKFORCE_WHATIF_QUESTIONS = [
  { category: 'Hiring Scenarios', question: 'If you hire 2 new RNCMs next month, how would capacity utilization change?', difficulty: 'medium' },
  { category: 'Resignation Impact', question: 'What happens to team workload if your highest-performing RNCM resigns?', difficulty: 'high' },
  { category: 'Team Redistribution', question: 'How would you redistribute 15 patients if one nurse goes on medical leave?', difficulty: 'high' },
  { category: 'PRN Dependency', question: 'At what PRN utilization percentage should you consider full-time hiring?', difficulty: 'medium' }
];

const BURNOUT_PREDICTION_QUESTIONS = [
  { category: 'Individual Risk', question: 'Which nurse shows the highest burnout risk trajectory over the next 4 weeks?', difficulty: 'high' },
  { category: 'Team Risk', question: 'Which team has compounding burnout factors that could lead to mass resignation?', difficulty: 'high' },
  { category: 'Intervention Timing', question: 'When is the optimal time to intervene before burnout becomes irreversible?', difficulty: 'medium' },
  { category: 'Prevention Strategies', question: 'What workload adjustments would reduce burnout risk by 50%?', difficulty: 'medium' }
];

const CENSUS_IMPACT_QUESTIONS = [
  { category: 'Census Growth', question: 'If census grows by 20% over 3 months, how many additional nurses are needed?', difficulty: 'medium' },
  { category: 'Census Decline', question: 'At what census level should you reduce staff to maintain profitability?', difficulty: 'high' },
  { category: 'Seasonal Patterns', question: 'How does winter census surge historically affect your burnout metrics?', difficulty: 'medium' },
  { category: 'Emergency Surge', question: 'How would an unexpected 30% referral increase impact operations?', difficulty: 'high' }
];

const HIRING_TIMELINE_QUESTIONS = [
  { category: 'Immediate Hiring', question: 'Based on current burnout scores, do you need to hire immediately?', difficulty: 'medium' },
  { category: '30-Day Projection', question: 'What will capacity utilization look like in 30 days without new hires?', difficulty: 'medium' },
  { category: '90-Day Forecast', question: 'How many nurses should be in the pipeline for the next quarter?', difficulty: 'high' },
  { category: 'Budget Impact', question: 'What is the cost of delayed hiring vs. overtime and PRN usage?', difficulty: 'high' }
];

const TEAM_REBALANCING_QUESTIONS = [
  { category: 'Caseload Balance', question: 'Which nurses should exchange patients to achieve optimal balance?', difficulty: 'high' },
  { category: 'Geographic Optimization', question: 'How would geographic reassignment reduce travel time and increase visits?', difficulty: 'medium' },
  { category: 'Specialty Matching', question: 'Which patients would benefit from reassignment to specialty-trained nurses?', difficulty: 'medium' },
  { category: 'Risk Reduction', question: 'What patient movements would reduce overall team burnout risk?', difficulty: 'high' }
];

// Get questions for simulation type
const getQuestionsForMode = (mode) => {
  switch (mode) {
    case 'budget_forecast':
      return BUDGET_QUESTIONS;
    case 'patient_load':
      return PATIENT_LOAD_QUESTIONS;
    case 'staffing_optimization':
      return STAFFING_QUESTIONS;
    case 'workforce_whatif':
      return WORKFORCE_WHATIF_QUESTIONS;
    case 'burnout_prediction':
      return BURNOUT_PREDICTION_QUESTIONS;
    case 'census_impact':
      return CENSUS_IMPACT_QUESTIONS;
    case 'hiring_timeline':
      return HIRING_TIMELINE_QUESTIONS;
    case 'team_rebalancing':
      return TEAM_REBALANCING_QUESTIONS;
    default:
      return CHAP_QUESTIONS;
  }
};

// Sample unannounced survey scenarios
const UNANNOUNCED_SCENARIOS = [
  { scenario: 'Surveyor arrives at 7:30 AM requesting immediate access to patient records', priority: 'critical' },
  { scenario: 'Random patient selected for comprehensive chart review', priority: 'high' },
  { scenario: 'Staff competency verification - CPR certification check', priority: 'medium' },
  { scenario: 'Emergency preparedness drill evaluation', priority: 'high' }
];

export default function AISimulations() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeMode, setActiveMode] = useState(null);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [aiResponse, setAiResponse] = useState('');
  const [generating, setGenerating] = useState(false);
  const [simulationResults, setSimulationResults] = useState(null);
  const [activeTab, setActiveTab] = useState('modes');
  const [simulationHistory, setSimulationHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Simulation settings
  const [settings, setSettings] = useState({
    difficulty: 'medium',
    includeFollowups: true,
    timeLimit: 30,
    categoryFocus: 'all'
  });

  // What-If Analysis State
  const [whatIfData, setWhatIfData] = useState(null);
  const [whatIfLoading, setWhatIfLoading] = useState(false);
  const [whatIfAnalysis, setWhatIfAnalysis] = useState(null);
  const [whatIfGenerating, setWhatIfGenerating] = useState(false);
  const [whatIfInputs, setWhatIfInputs] = useState({
    censusChange: 15,
    medicareRateChange: -3,
    prnUtilizationChange: 5,
    targetUtilization: 80,
    rncmChange: 0
  });
  const [whatIfSimResult, setWhatIfSimResult] = useState(null);

  // Fetch simulation history
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/simulations/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSimulationHistory(data.history || []);
        setHistoryStats(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [token]);

  // Fetch What-If current data from live sources
  const fetchWhatIfData = async () => {
    setWhatIfLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai-intelligence/whatif/current-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWhatIfData(data);
      } else {
        toast.error('Failed to load current data');
      }
    } catch (error) {
      console.error('Error fetching What-If data:', error);
      toast.error('Error loading current data');
    } finally {
      setWhatIfLoading(false);
    }
  };

  // Run What-If simulation when inputs change
  const runWhatIfSimulation = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/ai-intelligence/whatif/simulate`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          census_change_pct: whatIfInputs.censusChange,
          medicare_rate_change_pct: whatIfInputs.medicareRateChange,
          prn_utilization_change_pct: whatIfInputs.prnUtilizationChange,
          target_utilization_pct: whatIfInputs.targetUtilization,
          rncm_change: whatIfInputs.rncmChange
        })
      });
      if (response.ok) {
        const data = await response.json();
        setWhatIfSimResult(data);
      }
    } catch (error) {
      console.error('Error running simulation:', error);
    }
  };

  // Generate full What-If analysis with AI
  const generateFullWhatIfAnalysis = async () => {
    setWhatIfGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/ai-intelligence/whatif/full-analysis`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          census_change_pct: whatIfInputs.censusChange,
          medicare_rate_change_pct: whatIfInputs.medicareRateChange,
          prn_utilization_change_pct: whatIfInputs.prnUtilizationChange,
          target_utilization_pct: whatIfInputs.targetUtilization,
          rncm_change: whatIfInputs.rncmChange
        })
      });
      if (response.ok) {
        const data = await response.json();
        setWhatIfAnalysis(data);
        setWhatIfSimResult(data);
        toast.success('Full analysis generated successfully');
      } else {
        toast.error('Failed to generate analysis');
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      toast.error('Error generating analysis');
    } finally {
      setWhatIfGenerating(false);
    }
  };

  // Fetch What-If data when tab is active
  useEffect(() => {
    if (activeTab === 'whatif' && !whatIfData) {
      fetchWhatIfData();
    }
  }, [activeTab, token]);

  // Run simulation when inputs change (debounced effect)
  useEffect(() => {
    if (activeTab === 'whatif' && whatIfData) {
      const timeout = setTimeout(() => {
        runWhatIfSimulation();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [whatIfInputs, activeTab, whatIfData]);

  // Save simulation result
  const saveSimulationResult = async (results) => {
    try {
      await fetch(`${API_URL}/api/simulations/results`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          simulation_type: results.mode,
          questions_answered: results.questionsAnswered,
          total_questions: results.totalQuestions,
          readiness_score: results.readinessScore,
          completion_rate: results.completionRate,
          duration_minutes: 25,
          answers: answers,
          ai_analysis: aiResponse
        })
      });
      fetchHistory(); // Refresh history
    } catch (error) {
      console.error('Error saving result:', error);
    }
  };

  const startSimulation = async (mode) => {
    setActiveMode(mode);
    setSimulationRunning(true);
    setCurrentQuestion(0);
    setAnswers({});
    setSimulationResults(null);
    setActiveTab('simulation');
    toast.success(`Starting ${SIMULATION_MODES[mode].name}`);
  };

  const handleAnswer = (questionIdx, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionIdx]: answer
    }));
  };

  const nextQuestion = () => {
    const questions = getQuestionsForMode(activeMode);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      completeSimulation();
    }
  };

  const completeSimulation = async () => {
    setGenerating(true);
    const questions = getQuestionsForMode(activeMode);
    
    try {
      const questions = getQuestionsForMode(activeMode);
      // Generate AI analysis of simulation results
      const response = await fetch(`${API_URL}/api/ai-agent/chat`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: `Analyze these ${SIMULATION_MODES[activeMode]?.name || 'survey simulation'} results and provide feedback:\n\nQuestions Answered: ${Object.keys(answers).length}/${questions.length}\n\nProvide:\n1. Overall readiness score (0-100)\n2. Areas of strength\n3. Areas needing improvement\n4. Specific recommendations\n5. Priority action items`,
          session_id: `sim-${Date.now()}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.response || data.message);
        
        // Calculate mock results
        const completionRate = (Object.keys(answers).length / questions.length) * 100;
        const results = {
          mode: activeMode,
          completionRate,
          questionsAnswered: Object.keys(answers).length,
          totalQuestions: questions.length,
          readinessScore: Math.round(70 + Math.random() * 25), // Mock score
          completedAt: new Date().toISOString()
        };
        setSimulationResults(results);
        
        // Save to history
        await saveSimulationResult(results);
        toast.success('Simulation completed and saved to history');
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      toast.error('Failed to generate analysis');
    } finally {
      setGenerating(false);
      setSimulationRunning(false);
    }
  };

  const resetSimulation = () => {
    setActiveMode(null);
    setSimulationRunning(false);
    setCurrentQuestion(0);
    setAnswers({});
    setAiResponse('');
    setSimulationResults(null);
    setActiveTab('modes');
  };

  const getModeConfig = () => SIMULATION_MODES[activeMode] || {};

  // Radar chart data for readiness
  const readinessData = [
    { category: 'Documentation', score: 85, fullMark: 100 },
    { category: 'Clinical Care', score: 78, fullMark: 100 },
    { category: 'Safety', score: 92, fullMark: 100 },
    { category: 'Compliance', score: 88, fullMark: 100 },
    { category: 'Training', score: 75, fullMark: 100 },
    { category: 'Emergency Prep', score: 82, fullMark: 100 }
  ];

  return (
    <div className="space-y-6" data-testid="ai-simulations-page">
      {/* Header */}
      <PageHeader
        icon={Brain}
        title="Advanced AI Simulations"
        subtitle="CHAP Survey, Mock Unannounced Survey, and Recertification Audit simulations"
        gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
      >
        {simulationRunning && (
          <Button 
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={resetSimulation}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Exit Simulation
          </Button>
        )}
      </PageHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="modes" className={modernTabTriggerClass}>
            <Play className="h-4 w-4 mr-2" /> Simulation Modes
          </TabsTrigger>
          <TabsTrigger value="simulation" className={modernTabTriggerClass} disabled={!activeMode}>
            <Activity className="h-4 w-4 mr-2" /> Active Simulation
          </TabsTrigger>
          <TabsTrigger value="results" className={modernTabTriggerClass}>
            <Target className="h-4 w-4 mr-2" /> Results & History
          </TabsTrigger>
          <TabsTrigger value="settings" className={modernTabTriggerClass}>
            <Settings2 className="h-4 w-4 mr-2" /> Settings
          </TabsTrigger>
          <TabsTrigger value="whatif" className={modernTabTriggerClass}>
            <Sliders className="h-4 w-4 mr-2" /> What-If Analysis
          </TabsTrigger>
        </TabsList>

        {/* Simulation Modes Tab */}
        <TabsContent value="modes" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {Object.entries(SIMULATION_MODES).map(([key, mode]) => {
              const Icon = mode.icon;
              return (
                <ModernCard key={key} className="hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mode.gradient} flex items-center justify-center mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle>{mode.name}</CardTitle>
                    <CardDescription>{mode.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Duration</span>
                      <Badge variant="outline">{mode.duration}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {mode.categories.slice(0, 4).map((cat, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{cat}</Badge>
                      ))}
                    </div>
                    <Button 
                      className={`w-full bg-gradient-to-r ${mode.gradient}`}
                      onClick={() => startSimulation(key)}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start Simulation
                    </Button>
                  </CardContent>
                </ModernCard>
              );
            })}
          </div>

          {/* Readiness Overview */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={Target} title="Overall Readiness Score" subtitle="Based on recent simulations and data" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={readinessData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="category" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} />
                    <Radar name="Readiness" dataKey="score" stroke="#6366F1" fill="#6366F1" fillOpacity={0.5} />
                  </RadarChart>
                </ResponsiveContainer>
                <div className="space-y-4">
                  {readinessData.map((item, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{item.category}</span>
                        <span className={item.score >= 80 ? 'text-emerald-600' : item.score >= 60 ? 'text-amber-600' : 'text-red-600'}>
                          {item.score}%
                        </span>
                      </div>
                      <Progress value={item.score} className="h-2" />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Active Simulation Tab */}
        <TabsContent value="simulation" className="space-y-6">
          {activeMode && (
            <>
              {/* Simulation Header */}
              <Card className={`bg-gradient-to-r ${getModeConfig().gradient} text-white`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">{getModeConfig().name}</h2>
                      <p className="text-white/80">Question {currentQuestion + 1} of {getQuestionsForMode(activeMode).length}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">{Math.round((currentQuestion / getQuestionsForMode(activeMode).length) * 100)}%</div>
                      <p className="text-white/80">Complete</p>
                    </div>
                  </div>
                  <Progress value={(currentQuestion / getQuestionsForMode(activeMode).length) * 100} className="mt-4 bg-white/30" />
                </CardContent>
              </Card>

              {/* Current Question */}
              {simulationRunning && !generating && (
                <ModernCard>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{getQuestionsForMode(activeMode)[currentQuestion]?.category}</Badge>
                      <Badge className={
                        getQuestionsForMode(activeMode)[currentQuestion]?.difficulty === 'hard' ? 'bg-red-500' :
                        getQuestionsForMode(activeMode)[currentQuestion]?.difficulty === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                      }>
                        {getQuestionsForMode(activeMode)[currentQuestion]?.difficulty}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-lg font-medium">{getQuestionsForMode(activeMode)[currentQuestion]?.question}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Your Response</Label>
                      <textarea 
                        className="w-full p-3 border rounded-lg min-h-[120px] bg-background"
                        placeholder="Type your response here..."
                        value={answers[currentQuestion] || ''}
                        onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                      />
                    </div>

                    <div className="flex justify-between">
                      <Button variant="outline" onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))} disabled={currentQuestion === 0}>
                        Previous
                      </Button>
                      <Button onClick={nextQuestion} className={`bg-gradient-to-r ${getModeConfig().gradient}`}>
                        {currentQuestion === getQuestionsForMode(activeMode).length - 1 ? 'Complete Simulation' : 'Next Question'}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </ModernCard>
              )}

              {/* Generating Results */}
              {generating && (
                <ModernCard>
                  <CardContent className="py-12">
                    <div className="text-center">
                      <Loader2 className="h-12 w-12 animate-spin mx-auto text-[#00b0ab]" />
                      <p className="mt-4 text-lg font-medium">Analyzing your responses...</p>
                      <p className="text-muted-foreground">AI is generating your simulation results</p>
                    </div>
                  </CardContent>
                </ModernCard>
              )}

              {/* Simulation Results */}
              {simulationResults && aiResponse && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <MetricCard
                      title="Readiness Score"
                      value={`${simulationResults.readinessScore}%`}
                      icon={Target}
                      gradient={simulationResults.readinessScore >= 80 ? 'emerald' : simulationResults.readinessScore >= 60 ? 'amber' : 'rose'}
                    />
                    <MetricCard
                      title="Questions Answered"
                      value={simulationResults.questionsAnswered}
                      subtitle={`of ${simulationResults.totalQuestions}`}
                      icon={CheckCircle2}
                      gradient="blue"
                    />
                    <MetricCard
                      title="Completion Rate"
                      value={`${simulationResults.completionRate.toFixed(0)}%`}
                      icon={Activity}
                      gradient="indigo"
                    />
                    <MetricCard
                      title="Time Spent"
                      value="25 min"
                      icon={Timer}
                      gradient="purple"
                    />
                  </div>

                  <ModernCard>
                    <CardHeader>
                      <SectionHeader icon={Brain} title="AI Analysis & Recommendations" />
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px] rounded-lg border p-4 bg-muted/30">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown>{aiResponse}</ReactMarkdown>
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </ModernCard>

                  <div className="flex justify-center gap-4">
                    <Button variant="outline" onClick={resetSimulation}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      New Simulation
                    </Button>
                    <Button className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]">
                      <BookOpen className="h-4 w-4 mr-2" />
                      View Full Report
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {!activeMode && (
            <div className="text-center py-12 text-muted-foreground">
              <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No Active Simulation</p>
              <p className="text-sm mt-2">Select a simulation mode to begin</p>
              <Button className="mt-4" onClick={() => setActiveTab('modes')}>
                Choose Simulation Mode
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-6">
          {/* Statistics Cards */}
          {historyStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                title="Total Simulations"
                value={historyStats.total_simulations}
                icon={Target}
                gradient="indigo"
              />
              <MetricCard
                title="Average Score"
                value={`${historyStats.average_score}%`}
                icon={Activity}
                gradient={historyStats.average_score >= 80 ? 'emerald' : historyStats.average_score >= 60 ? 'amber' : 'rose'}
              />
              <MetricCard
                title="Best Score"
                value={`${historyStats.best_score}%`}
                icon={CheckCircle2}
                gradient="emerald"
              />
              <MetricCard
                title="Trend"
                value={historyStats.trend === 'improving' ? 'Improving' : historyStats.trend === 'declining' ? 'Declining' : 'Stable'}
                icon={historyStats.trend === 'improving' ? TrendingUp : Activity}
                gradient={historyStats.trend === 'improving' ? 'emerald' : historyStats.trend === 'declining' ? 'rose' : 'blue'}
              />
            </div>
          )}

          {/* History Table */}
          <ModernCard>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Simulation History</CardTitle>
                  <CardDescription>Review past simulation results and track improvement</CardDescription>
                </div>
                <Button variant="outline" onClick={fetchHistory} disabled={loadingHistory}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
                </div>
              ) : simulationHistory.length > 0 ? (
                <div className="space-y-4">
                  {simulationHistory.map((sim, idx) => (
                    <div 
                      key={sim.id || idx}
                      className="p-4 rounded-lg border bg-slate-50 dark:bg-slate-800/50 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`${
                              sim.simulation_type === 'chap_survey' ? 'bg-blue-500' :
                              sim.simulation_type === 'unannounced_survey' ? 'bg-amber-500' : 'bg-emerald-500'
                            } text-white`}>
                              {SIMULATION_MODES[sim.simulation_type]?.name || sim.simulation_type}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {new Date(sim.completed_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {sim.questions_answered}/{sim.total_questions} questions • {sim.duration_minutes || 0} minutes
                          </p>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${
                            sim.readiness_score >= 80 ? 'text-emerald-600' :
                            sim.readiness_score >= 60 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {sim.readiness_score}%
                          </div>
                          <p className="text-xs text-muted-foreground">Readiness Score</p>
                        </div>
                      </div>
                      {sim.ai_analysis && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {sim.ai_analysis.substring(0, 150)}...
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No Simulation History</p>
                  <p className="text-sm mt-2">Complete simulations to see your results here</p>
                </div>
              )}
            </CardContent>
          </ModernCard>

          {/* Trend Chart */}
          {simulationHistory.length > 2 && (
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={TrendingUp} title="Score Trend" subtitle="Track your improvement over time" />
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={[...simulationHistory].reverse().map((s, i) => ({
                    index: i + 1,
                    score: s.readiness_score,
                    type: s.simulation_type
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="index" label={{ value: 'Simulation #', position: 'insideBottom', offset: -5 }} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1' }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </ModernCard>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <ModernCard>
            <CardHeader>
              <CardTitle>Simulation Settings</CardTitle>
              <CardDescription>Configure your simulation experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Default Difficulty</Label>
                  <Select value={settings.difficulty} onValueChange={(v) => setSettings({...settings, difficulty: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Time Limit (minutes): {settings.timeLimit}</Label>
                  <Slider
                    value={[settings.timeLimit]}
                    onValueChange={([v]) => setSettings({...settings, timeLimit: v})}
                    min={15}
                    max={60}
                    step={5}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Include Follow-up Questions</Label>
                    <p className="text-sm text-muted-foreground">AI will ask clarifying questions</p>
                  </div>
                  <Switch
                    checked={settings.includeFollowups}
                    onCheckedChange={(v) => setSettings({...settings, includeFollowups: v})}
                  />
                </div>
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* What-If Analysis Tab */}
        <TabsContent value="whatif" className="space-y-6" data-testid="whatif-tab">
          {whatIfLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
            </div>
          ) : (
            <>
              {/* Current Data Banner */}
              {whatIfData && (
                <Card className="bg-gradient-to-r from-slate-900 to-indigo-900 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white/70">Live Data Baseline</p>
                        <p className="text-lg font-semibold">
                          ADC: {whatIfData.census.current_adc} | Staff: {whatIfData.workforce.total_rncm} RNCMs | 
                          Revenue: ${(whatIfData.financial.weekly_revenue / 1000).toFixed(1)}K/wk
                        </p>
                      </div>
                      <Badge variant="outline" className="border-white/30 text-white">
                        {whatIfData.data_sources.census === 'defaults' ? 'Sample Data' : 'Live Data'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Census Impact Simulator */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Users} title="Census Impact" subtitle="Model census change scenarios" />
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Census Change</Label>
                          <span className={`text-sm font-medium ${whatIfInputs.censusChange >= 0 ? 'text-[#00b0ab]' : 'text-rose-600'}`}>
                            {whatIfInputs.censusChange >= 0 ? '+' : ''}{whatIfInputs.censusChange}%
                          </span>
                        </div>
                        <Slider 
                          value={[whatIfInputs.censusChange]} 
                          onValueChange={([v]) => setWhatIfInputs({...whatIfInputs, censusChange: v})}
                          min={-30} 
                          max={50} 
                          step={5}
                          data-testid="census-slider"
                        />
                        <p className="text-xs text-muted-foreground">
                          Current ADC: {whatIfData?.census.current_adc || 50} → Projected: {
                            whatIfSimResult?.simulated?.adc || 
                            ((whatIfData?.census.current_adc || 50) * (1 + whatIfInputs.censusChange / 100)).toFixed(1)
                          }
                        </p>
                      </div>
                      
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                        <h4 className="font-medium text-sm">Projected Impact:</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded">
                            <p className="text-xs text-muted-foreground">Revenue</p>
                            <p className={`font-semibold ${(whatIfSimResult?.deltas?.weekly_revenue || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {(whatIfSimResult?.deltas?.weekly_revenue || 0) >= 0 ? '+' : ''}
                              ${((whatIfSimResult?.deltas?.weekly_revenue || 0) / 1000).toFixed(1)}K/wk
                            </p>
                          </div>
                          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded">
                            <p className="text-xs text-muted-foreground">Staff Utilization</p>
                            <p className={`font-semibold ${(whatIfSimResult?.simulated?.staff_utilization || 0) > 85 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {whatIfSimResult?.simulated?.staff_utilization?.toFixed(1) || '77.2'}%
                              {(whatIfSimResult?.simulated?.staff_utilization || 0) > 85 ? ' (High)' : ''}
                            </p>
                          </div>
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
                            <p className="text-xs text-muted-foreground">Visits Required</p>
                            <p className="font-semibold text-blue-600">
                              {(whatIfSimResult?.deltas?.weekly_visits || 0) >= 0 ? '+' : ''}
                              {whatIfSimResult?.deltas?.weekly_visits?.toFixed(0) || '0'}/week
                            </p>
                          </div>
                          <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded">
                            <p className="text-xs text-muted-foreground">Burnout Risk</p>
                            <p className="font-semibold text-rose-600">
                              {whatIfSimResult?.baseline?.burnout_risk_count || 0} → {whatIfSimResult?.simulated?.burnout_risk_count || 0} staff
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </ModernCard>

                {/* Financial Scenario Simulator */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Building} title="Financial Scenario" subtitle="Model cost and revenue changes" />
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Medicare Rate Change</Label>
                          <span className={`text-sm font-medium ${whatIfInputs.medicareRateChange >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {whatIfInputs.medicareRateChange >= 0 ? '+' : ''}{whatIfInputs.medicareRateChange}%
                          </span>
                        </div>
                        <Slider 
                          value={[whatIfInputs.medicareRateChange]} 
                          onValueChange={([v]) => setWhatIfInputs({...whatIfInputs, medicareRateChange: v})}
                          min={-10} 
                          max={10} 
                          step={1}
                          data-testid="medicare-slider"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>PRN Utilization Change</Label>
                          <span className={`text-sm font-medium ${whatIfInputs.prnUtilizationChange >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {whatIfInputs.prnUtilizationChange >= 0 ? '+' : ''}{whatIfInputs.prnUtilizationChange}%
                          </span>
                        </div>
                        <Slider 
                          value={[whatIfInputs.prnUtilizationChange]} 
                          onValueChange={([v]) => setWhatIfInputs({...whatIfInputs, prnUtilizationChange: v})}
                          min={-10} 
                          max={20} 
                          step={1}
                          data-testid="prn-slider"
                        />
                      </div>
                      
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                        <h4 className="font-medium text-sm">Financial Impact:</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Net Margin Impact</span>
                            <Badge className={`${(whatIfSimResult?.deltas?.net_margin_pct || 0) >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                              {(whatIfSimResult?.deltas?.net_margin_pct || 0) >= 0 ? '+' : ''}
                              {whatIfSimResult?.deltas?.net_margin_pct?.toFixed(1) || '0'}%
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Annual Revenue</span>
                            <span className={`text-sm font-medium ${(whatIfSimResult?.deltas?.weekly_revenue || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {(whatIfSimResult?.deltas?.weekly_revenue || 0) >= 0 ? '+' : ''}
                              ${(((whatIfSimResult?.deltas?.weekly_revenue || 0) * 52) / 1000).toFixed(0)}K
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Breakeven Census</span>
                            <span className="text-sm font-medium">
                              {whatIfSimResult?.baseline?.breakeven_adc?.toFixed(1) || '32'} → {whatIfSimResult?.simulated?.breakeven_adc?.toFixed(1) || '32'} ADC
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </ModernCard>

                {/* Staffing Model Simulator */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Stethoscope} title="Staffing Model" subtitle="Optimize staff allocation" />
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Target Utilization (%)</Label>
                        <Slider 
                          value={[whatIfInputs.targetUtilization]} 
                          onValueChange={([v]) => setWhatIfInputs({...whatIfInputs, targetUtilization: v})}
                          min={60} 
                          max={95} 
                          step={5}
                          data-testid="utilization-slider"
                        />
                        <p className="text-xs text-muted-foreground">
                          Current: {whatIfData?.workforce.staff_utilization || 77.2}% | Target: {whatIfInputs.targetUtilization}%
                        </p>
                      </div>
                      
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                        <h4 className="font-medium text-sm">Staffing Recommendations:</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                            <span>FTE Gap</span>
                            <span className={`font-medium ${(whatIfSimResult?.deltas?.fte_gap || 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {(whatIfSimResult?.deltas?.fte_gap || 0) > 0 ? '+' : ''}{whatIfSimResult?.deltas?.fte_gap || 0} FTE
                            </span>
                          </div>
                          <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <span>Avg Caseload</span>
                            <span className="font-medium text-blue-600">
                              {whatIfSimResult?.simulated?.avg_caseload?.toFixed(1) || (whatIfData?.workforce.avg_caseload || 12.5)} patients/RNCM
                            </span>
                          </div>
                          <div className="flex items-center justify-between p-2 bg-violet-50 dark:bg-violet-900/20 rounded">
                            <span>Weekly Revenue</span>
                            <span className="font-medium text-[#00b0ab]">
                              ${((whatIfSimResult?.simulated?.weekly_revenue || whatIfData?.financial.weekly_revenue || 0) / 1000).toFixed(1)}K
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </ModernCard>

                {/* AI Recommendations */}
                <ModernCard>
                  <CardHeader>
                    <SectionHeader icon={Sparkles} title="AI Recommendations" subtitle="Based on your scenario inputs" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {whatIfSimResult?.recommendations?.length > 0 ? (
                        whatIfSimResult.recommendations.map((rec, idx) => (
                          <div key={idx} className={`p-3 border-l-4 rounded-r-lg ${
                            rec.priority === 'high' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20' :
                            rec.priority === 'medium' ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' :
                            'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          }`}>
                            <h4 className={`font-medium text-sm ${
                              rec.priority === 'high' ? 'text-rose-700 dark:text-rose-400' :
                              rec.priority === 'medium' ? 'text-amber-700 dark:text-amber-400' :
                              'text-emerald-700 dark:text-emerald-400'
                            }`}>{rec.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">{rec.detail}</p>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="p-3 border-l-4 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 rounded-r-lg">
                            <h4 className="font-medium text-sm text-emerald-700 dark:text-emerald-400">Scenario Ready</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              Adjust the sliders to model different scenarios. AI recommendations will appear based on your inputs.
                            </p>
                          </div>
                        </>
                      )}
                      
                      {whatIfSimResult?.risk_alerts?.length > 0 && (
                        whatIfSimResult.risk_alerts.map((alert, idx) => (
                          <div key={`alert-${idx}`} className={`p-3 border-l-4 rounded-r-lg ${
                            alert.severity === 'critical' ? 'border-red-600 bg-red-50 dark:bg-red-900/20' :
                            'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          }`}>
                            <h4 className={`font-medium text-sm ${
                              alert.severity === 'critical' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'
                            }`}>{alert.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">{alert.detail}</p>
                          </div>
                        ))
                      )}
                    </div>
                    <Button 
                      className="w-full mt-4 bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                      onClick={generateFullWhatIfAnalysis}
                      disabled={whatIfGenerating}
                      data-testid="generate-analysis-btn"
                    >
                      {whatIfGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating Analysis...
                        </>
                      ) : (
                        <>
                          <Brain className="h-4 w-4 mr-2" />
                          Generate Full Analysis
                        </>
                      )}
                    </Button>
                  </CardContent>
                </ModernCard>
              </div>

              {/* Full Analysis Results */}
              {whatIfAnalysis && (
                <ModernCard className="mt-6">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <SectionHeader icon={Brain} title="AI-Powered Full Analysis" subtitle="Comprehensive scenario evaluation" />
                      <Badge variant={whatIfAnalysis.ai_powered ? 'default' : 'secondary'}>
                        {whatIfAnalysis.ai_powered ? 'AI Enhanced' : 'Automated'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px] rounded-lg border p-4 bg-muted/30">
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown>{whatIfAnalysis.ai_analysis}</ReactMarkdown>
                      </div>
                    </ScrollArea>
                  </CardContent>
                </ModernCard>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
